import { useEffect, useState } from 'react'

import { LocalStorageKey } from 'shared/constants/localStorage'
import { DesktopGridItem } from 'shared/types/desktop-grid'
import { lsGetItem, lsSetItem } from 'shared/utils/local-storage'

import { useDesktopStore, useSelectionStore } from '../../../stores'
import { resolveLayoutMode } from '../utils'
import type { LayoutMode } from '../utils/reflow/types'

const dateReviver = (key: string, value: unknown): unknown => {
  if (key === 'createdAt' && typeof value === 'string') {
    return new Date(value)
  }
  return value
}

// Each viewport class owns its own arrangement slot. Sequential (mobile)
// rearrangements are user intent just like desktop ones, but the two layouts
// must never overwrite each other — a packed mobile session persisting into
// the desktop slot is how the desktop arrangement used to get destroyed.
const modeStorageKey = (
  mode: LayoutMode
): LocalStorageKey.DESKTOP_GRID | LocalStorageKey.DESKTOP_GRID_MOBILE =>
  mode === 'sequential'
    ? LocalStorageKey.DESKTOP_GRID_MOBILE
    : LocalStorageKey.DESKTOP_GRID

type UseDesktopItemsPersistenceOptions = {
  userId: number | undefined
  items: DesktopGridItem[]
}

/**
 * Loads the per-user desktop layout from the current viewport mode's
 * localStorage slot when the user or the viewport class changes
 * (deduplicating by id), and persists it back to that same slot whenever
 * items change.
 */
export const useDesktopItemsPersistence = ({
  userId,
  items
}: UseDesktopItemsPersistenceOptions) => {
  const clearSelection = useSelectionStore(state => state.clear)
  const setItems = useDesktopStore(state => state.setItems)

  const [viewportMode, setViewportMode] = useState<LayoutMode>(() =>
    resolveLayoutMode(window.innerWidth)
  )

  useEffect(() => {
    const handleResize = () =>
      setViewportMode(resolveLayoutMode(window.innerWidth))
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  useEffect(() => {
    // Switching users or viewport class replaces the item set, so any prior
    // selection is stale.
    clearSelection()
    if (typeof userId !== 'number') {
      setItems([])
      return
    }
    // First session in this mode: start from the other mode's arrangement —
    // the reflow derives this mode's layout from it.
    const fallbackKey = modeStorageKey(
      viewportMode === 'sequential' ? 'desktop' : 'sequential'
    )
    const saved: DesktopGridItem[] =
      lsGetItem(modeStorageKey(viewportMode), dateReviver)?.[userId] ??
      lsGetItem(fallbackKey, dateReviver)?.[userId] ??
      []
    // dedupe by id — covers shortcut collisions (id == fileId.toString()).
    const seenIds = new Set<string>()
    const dedupedById = saved.filter(item => {
      if (seenIds.has(item.id)) return false
      seenIds.add(item.id)
      return true
    })
    // Defensive: a user who hasn't gone through useMigrateGridWidgetsToSidebar
    // yet may still have leftover `itemType: 'widget'` rows in this slot —
    // filter them out rather than let a non-shortcut shape reach the grid.
    const shortcutsOnly = dedupedById.filter(
      item => (item.itemType as string) !== 'widget'
    )
    // Raw store write, NOT commitManualItems: this is hydration of display
    // state, not a user action — it must not rewrite the base layout.
    setItems(shortcutsOnly)
  }, [userId, viewportMode, setItems, clearSelection])

  useEffect(() => {
    if (typeof userId !== 'number') return
    // Resolve the mode at write time: the items being persisted were laid
    // out for the viewport as it is now.
    const key = modeStorageKey(resolveLayoutMode(window.innerWidth))
    lsSetItem(key, {
      ...(lsGetItem(key) ?? {}),
      [userId]: items
    })
  }, [items, userId])
}
